import React, { useState, useEffect, useContext, useCallback } from "react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey, Role as CustomerRole } from "src/network/User";
import { useForm } from "react-hook-form";
import { format as formatDate } from "date-fns";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useRegisterRef } from "src/utils/Form";
import { MessageType } from "src/components/notifications/Notifier";
import { canManageCustomerPassword, getCustomerPasswordRole } from "./customerPasswordAccess";
import { CopyToClipboardButton } from "src/components/copy-to-clipboard/CopyToClipboardButton";
import classes from "./CustomerLoginInfo.module.css";

interface CustomerLoginInfoProps {
    hostname: string;
    username: string;
    password?: string;
    passwordHidden: boolean;
    userRole?: CustomerRole;
    customerId?: string;
    customerEmail?: string | null | undefined;
    lastSentEmail?: string | null;
}

interface SendEmailFormFields {
    emailRecipient?: string;
}

const CustomerLoginInfo = (props: CustomerLoginInfoProps) => {
    const { authStore, uiStore } = useContext(RootStoreContext);
    const canManagePassword = canManageCustomerPassword(props.userRole, authStore.user?.availableRoles);
    const passwordRole = getCustomerPasswordRole(authStore.user?.role, authStore.user?.availableRoles);
    const [copyButton, setCopyButton] = useState("Kopieren");
    const [isGettingPassword, setIsGettingPassword] = useState(false);
    const [password, setPassword] = useState<string | null>("***********************");
    const [hasReceivedPassword, setHasReceivedPasswort] = useState<boolean>(false);
    const [isGeneratePasswordModalDisplaying, setIsGeneratePasswordModalDisplaying] = useState(false);
    const [isInputEmailModalDisplaying, setisInputEmailModalDisplaying] = useState<boolean>(false);
    const [validationErrorMessage, setValidationErrorMessage] = useState("");
    const [email, setEmail] = useState<string | undefined>(props.customerEmail ?? undefined);
    const [lastSentEmailTimestamp, setLastSentEmailTimestamp] = useState<Date | undefined>(undefined);
    const [isSendEmailLoading, setIsSendEmailLoading] = useState(false);
    const [isSendEmailDisabled, setIsSendEmailDisabled] = useState(false);

    const isPasswordVisible = hasReceivedPassword || !props.passwordHidden;

    const toggleInputEmailModal = useCallback(() => {
        if (isInputEmailModalDisplaying) {
            setEmail(props.customerEmail ?? undefined);
        }
        setisInputEmailModalDisplaying((isDisplaying) => !isDisplaying);
    }, [isInputEmailModalDisplaying, props.customerEmail]);

    const SEND_EMAIL_SCHEMA = yup
        .object()
        .shape({
            emailRecipient: yup.string().email("Das Format der E-Mail-Adresse ist ungültig").required("Pflichtfeld!")
        })
        .required();

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<SendEmailFormFields>({
        mode: "onChange",
        resolver: yupResolver(SEND_EMAIL_SCHEMA)
    });
    const registerRef = useRegisterRef(register);

    useEffect(() => {
        if (!props.passwordHidden && props.password) {
            setPassword(props.password);
        }
    }, [props]);

    useEffect(() => {
        const checkSendEmailDisabledStatus = () => {
            const lastSentTimestamp = lastSentEmailTimestamp !== undefined ? lastSentEmailTimestamp.valueOf() : 0;
            const shouldBeDisabled = lastSentTimestamp !== 0 && Date.now() - lastSentTimestamp < 3 * 60 * 1000;
            if (!isSendEmailDisabled && shouldBeDisabled) {
                setIsSendEmailDisabled(true);
            }
            if (isSendEmailDisabled && !shouldBeDisabled) {
                setIsSendEmailDisabled(false);
            }
        };

        if (lastSentEmailTimestamp && lastSentEmailTimestamp !== undefined) {
            checkSendEmailDisabledStatus();
        }
        const interval = setInterval(checkSendEmailDisabledStatus, 20000);

        return () => {
            clearInterval(interval);
        };
    }, [lastSentEmailTimestamp, isSendEmailDisabled]);

    useEffect(() => {
        setLastSentEmailTimestamp(props.lastSentEmail ? new Date(props.lastSentEmail) : undefined);
    }, [props.lastSentEmail]);

    const copyToClipboard = useCallback(() => {
        const elem = document.createElement("textarea");
        const { username, hostname } = props;
        elem.value = `Benutzer=${username}\nPasswort=${password}\nHost=${hostname}`;
        document.body.appendChild(elem);
        elem.select();
        document.execCommand("copy");
        document.body.removeChild(elem);

        setCopyButton("In Zwischenablage gespeichert");

        setTimeout(() => {
            setCopyButton("Kopieren");
        }, 3000);
    }, [password, props]);

    const showErrorNotification = useCallback(
        (errorMsg: string) => {
            uiStore.enqueueSnackbar({
                content: errorMsg,
                options: {
                    type: "error",
                    autoClose: false
                }
            });
        },
        [uiStore]
    );

    const getPassword = useCallback(async () => {
        setIsGeneratePasswordModalDisplaying(false);

        setIsGettingPassword(true);

        if (authStore.user) {
            const accessToken = authStore.token;
            const tokenType = "Bearer";
            const role = passwordRole;

            try {
                const fetchResult = await fetch(`${NetworkConfig.generateNewERPPasswordUrl}/${props.customerId}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `${tokenType} ${accessToken}`,
                        "x-hasura-role": getRoleKey(role)
                    }
                });

                if (fetchResult.status === 200) {
                    try {
                        const returnedData = await fetchResult.json();
                        const { password } = returnedData.data;
                        setPassword(password);
                        setHasReceivedPasswort(true);
                    } catch (jsonError) {
                        console.error("jsonError: ", jsonError);
                    }
                } else {
                    const returnValue = await fetchResult.json();
                    showErrorNotification(returnValue.message);
                }
            } catch (fetchError) {
                showErrorNotification(fetchError.error);
            }
        }

        setIsGettingPassword(false);
    }, [
        authStore.user,
        authStore.token,
        passwordRole,
        props.customerId,
        showErrorNotification,
        setIsGeneratePasswordModalDisplaying
    ]);

    const onClickGeneratePasswordButton = useCallback(async () => {
        setIsGeneratePasswordModalDisplaying(true);
    }, [setIsGeneratePasswordModalDisplaying]);

    const toggleGeneratePasswordModal = useCallback(() => setIsGeneratePasswordModalDisplaying(false), []);

    const getFormattedDateAndTime = useCallback((date: Date | undefined) => {
        let datestring = "-";
        if (date) {
            const formattedDate = formatDate(date, "dd.MM.yyyy HH:mm");
            datestring = `${formattedDate.replace(" ", " um ")} Uhr`;
        }

        return datestring;
    }, []);

    const sendEmailWithNewPassword = useCallback(async () => {
        if (authStore.user) {
            const accessToken = authStore.token;
            const tokenType = "Bearer";
            const role = passwordRole;
            const requestBody = {
                Email: email
            };

            try {
                const fetchResult = await fetch(`${NetworkConfig.generateAndSendERPPasswordUrl}/${props.customerId}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `${tokenType} ${accessToken}`,
                        "x-hasura-role": getRoleKey(role)
                    },
                    body: JSON.stringify(requestBody)
                });
                if (fetchResult.status === 200) {
                    uiStore.printStatusMessage(`Die E-Mail wurde erfolgreich an ${email} geschickt`, MessageType.INFO);
                    setLastSentEmailTimestamp(new Date(Date.now()));
                } else {
                    const returnValue = await fetchResult.json();
                    showErrorNotification(returnValue.message);
                    uiStore.printStatusMessage(
                        `Es ist ein Fehler beim Versenden der E-Mail aufgetreten: ${returnValue.message}`,
                        MessageType.ERROR
                    );
                }
            } catch (fetchError) {
                uiStore.printStatusMessage(
                    `Es ist ein Fehler beim Versenden der E-Mail aufgetreten: ${fetchError.error}`,
                    MessageType.ERROR
                );
            }
        }
    }, [authStore.user, authStore.token, passwordRole, props.customerId, showErrorNotification, email, uiStore]);

    const handleEmailChange = useCallback(
        (event) => {
            const newEmail = event.target.value;

            SEND_EMAIL_SCHEMA.validate({ emailRecipient: newEmail })
                .then(() => {
                    setValidationErrorMessage("");
                })
                .catch((err) => {
                    setValidationErrorMessage(err.message);
                });
            setEmail(newEmail);
        },
        [SEND_EMAIL_SCHEMA]
    );

    const handleSubmitSendEmailWithNewPassword = useCallback(async () => {
        try {
            await SEND_EMAIL_SCHEMA.validate({ emailRecipient: email });
            setValidationErrorMessage("");
            sendEmailWithNewPassword();
            toggleInputEmailModal();
        } catch (err) {
            setValidationErrorMessage("Bitte geben Sie eine gültige E-Mail-Adresse ein");
            setIsSendEmailDisabled(false);
            localStorage.removeItem("isDisabled");
        }
    }, [SEND_EMAIL_SCHEMA, email, sendEmailWithNewPassword, toggleInputEmailModal]);

    const handleEmailButtonClick = useCallback(async () => {
        try {
            setIsSendEmailLoading(true);
            await handleSubmitSendEmailWithNewPassword();
        } catch (error) {
            console.error(error);
        } finally {
            setIsSendEmailLoading(false);
        }
    }, [handleSubmitSendEmailWithNewPassword]);

    const GeneratePasswordButton = (props: any) => {
        return (
            <div>
                <UI.Button
                    label="Neues Passwort generieren"
                    onClick={onClickGeneratePasswordButton}
                    hidden={!props.passwordHidden || !props.customerId || !canManagePassword}
                    disabled={!props.passwordHidden || isGettingPassword}
                    className="customer-button w-100"
                />

                <UI.Modal
                    backdrop={"static"}
                    title={"Neues Passwort generieren"}
                    show={isGeneratePasswordModalDisplaying}
                    onClose={toggleGeneratePasswordModal}
                >
                    <div>
                        <p>Wollen Sie wirklich das Passwort für den ERP-Benutzer neu generieren?"</p>
                        <UI.Button
                            label={"Ja"}
                            disabled={false}
                            onClick={getPassword}
                            className={`${classes.modalBtn} mr-2 customer-button`}
                            variant="danger"
                        />
                    </div>
                </UI.Modal>
            </div>
        );
    };

    return (
        <div>
            <div className={classes.infoBlock}>
                <p className={classes.infoHeading}>Hostname</p>
                <div className={classes.valueWithCopyButton}>
                    <p className={classes.infoText}>{props.hostname}</p>
                    <CopyToClipboardButton value={props.hostname} />
                </div>
            </div>
            <div className={classes.infoBlock}>
                <p className={classes.infoHeading}>Benutzername</p>
                <div className={classes.valueWithCopyButton}>
                    <p className={classes.infoText}>{props.username}</p>
                    <CopyToClipboardButton value={props.username} />
                </div>
            </div>
            <div className={classes.infoBlock}>
                <p className={classes.infoHeading}>Passwort</p>
                <div className={classes.valueWithCopyButton}>
                    {props.passwordHidden && isGettingPassword ? (
                        <UI.RotatingSpinner noLogo={true} size={20} className={"align-items-start"} />
                    ) : (
                        <p className={classes.infoText}>{password}</p>
                    )}
                    {isPasswordVisible && password ? <CopyToClipboardButton value={password} /> : undefined}
                </div>
                {canManagePassword ? (
                    <div className={classes.infoBlock}>
                        <p className={classes.infoHeading}>Zuletzt gesendet am</p>
                        <p className={classes.lastSentValue}>{getFormattedDateAndTime(lastSentEmailTimestamp)}</p>
                    </div>
                ) : undefined}
                <GeneratePasswordButton customerId={props.customerId} passwordHidden={props.passwordHidden} />
            </div>
            <div>
                <UI.Button
                    label="Neues Passwort + E-Mail"
                    onClick={toggleInputEmailModal}
                    hidden={!props.passwordHidden || !props.customerId || !canManagePassword}
                    disabled={!props.passwordHidden || isGettingPassword || isSendEmailDisabled || isSendEmailLoading}
                    className="customer-button w-100"
                />
                <UI.Modal
                    backdrop={"static"}
                    title={"Zugangsdaten per E-Mail an Kunde senden"}
                    show={isInputEmailModalDisplaying}
                    onClose={toggleInputEmailModal}
                >
                    <UI.Form onSubmit={handleSubmit(handleSubmitSendEmailWithNewPassword)}>
                        <UI.FormGroup controlId="formEmail" className="mb-3">
                            <UI.Form.Label title="Die Zugangsdaten werden an folgenden Empfänger versendet:"></UI.Form.Label>
                            <UI.Input
                                name="emailRecipient"
                                ref={registerRef}
                                type="email"
                                defaultValue={email}
                                onBlur={handleEmailChange}
                                isInvalid={errors.emailRecipient !== undefined}
                            />
                        </UI.FormGroup>
                        <UI.Button className="SubmitEmail" label={"Versenden"} onClick={handleEmailButtonClick} />
                        {isSendEmailLoading && (
                            <UI.RotatingSpinner noLogo={true} size={20} className={"align-items-start"} />
                        )}
                        {validationErrorMessage && (
                            <div style={{ color: "red", fontSize: "small", marginTop: "5px", marginBottom: "5px" }}>
                                {validationErrorMessage}
                            </div>
                        )}
                    </UI.Form>
                </UI.Modal>
            </div>
            <div>
                {isPasswordVisible ? (
                    <div className={`${classes.copyBlock} d-flex`}>
                        <UI.Icon icon={UI.SVGIcon.Documents} style={{ cursor: "pointer" }} onClick={copyToClipboard} />
                        <div>
                            <span>{copyButton}</span>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

CustomerLoginInfo.defaultProps = {
    passwordHidden: false
};

export default CustomerLoginInfo;
